import * as XLSX from "xlsx";

const MONTH_INDEXES = {
  jan: 0,
  january: 0,
  feb: 1,
  february: 1,
  mar: 2,
  march: 2,
  apr: 3,
  april: 3,
  may: 4,
  jun: 5,
  june: 5,
  jul: 6,
  july: 6,
  aug: 7,
  august: 7,
  sep: 8,
  sept: 8,
  september: 8,
  oct: 9,
  october: 9,
  nov: 10,
  november: 10,
  dec: 11,
  december: 11
};

const createStrictDate = (year, monthIndex, day) => {
  const date = new Date(0);
  date.setHours(0, 0, 0, 0);
  date.setFullYear(year, monthIndex, day);

  return date.getFullYear() === year &&
    date.getMonth() === monthIndex &&
    date.getDate() === day
    ? date
    : null;
};

const expandYear = (year) => {
  const text = String(year);
  const numericYear = Number(text);
  return text.length === 2 ? 2000 + numericYear : numericYear;
};

export const parseExcelDate = (value) => {
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : new Date(value.getTime());
  }

  // Excel stores cells formatted as dates as serial numbers.
  if (typeof value === "number" && Number.isFinite(value)) {
    const parsed = XLSX.SSF.parse_date_code(value);
    return parsed
      ? createStrictDate(parsed.y, parsed.m - 1, parsed.d)
      : null;
  }

  if (typeof value !== "string") return null;

  const text = value.trim().replace(/\s+/g, " ");
  if (!text) return null;

  let match = text.match(
    /^(\d{1,2})\s+([a-z]+)\s*,?\s*(\d{2}|\d{4})$/i
  );
  if (match) {
    const [, day, monthName, year] = match;
    const monthIndex = MONTH_INDEXES[monthName.toLowerCase()];
    return monthIndex === undefined
      ? null
      : createStrictDate(expandYear(year), monthIndex, Number(day));
  }

  match = text.match(
    /^([a-z]+)\s+(\d{1,2})\s*,?\s*(\d{2}|\d{4})$/i
  );
  if (match) {
    const [, monthName, day, year] = match;
    const monthIndex = MONTH_INDEXES[monthName.toLowerCase()];
    return monthIndex === undefined
      ? null
      : createStrictDate(expandYear(year), monthIndex, Number(day));
  }

  match = text.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/);
  if (match) {
    const [, year, month, day] = match;
    return createStrictDate(expandYear(year), Number(month) - 1, Number(day));
  }

  match = text.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{2}|\d{4})$/);
  if (match) {
    const [, day, month, year] = match;
    return createStrictDate(expandYear(year), Number(month) - 1, Number(day));
  }

  return null;
};

export const generateExcelMultipleSheets = ({
  fileName = "upload_template.xlsx",
  sheets
}) => {
  if (!sheets || Object.keys(sheets).length === 0) {
    throw new Error("No sheets provided");
  }

  // Create a new workbook
  const wb = XLSX.utils.book_new();

  // Support array format: [{name, data, type, columnWidths}]
  if (Array.isArray(sheets)) {
    sheets.forEach(({ name, data, type, columnWidths }) => {
      let ws;

      if (type === "aoa") {
        ws = XLSX.utils.aoa_to_sheet(data);
      } else {
        ws = XLSX.utils.json_to_sheet(data);
      }

      // Set column widths if provided
      if (columnWidths && Array.isArray(columnWidths)) {
        ws["!cols"] = columnWidths.map((w) => ({ wch: w }));
        // wch = "characters width"
      }

      XLSX.utils.book_append_sheet(wb, ws, name || "Sheet");
    });
  } else {
    // Support object format: {SheetName: {data, columnWidths}}
    Object.entries(sheets).forEach(([name, sheetConfig]) => {
      let data, columnWidths;
      if (Array.isArray(sheetConfig)) {
        data = sheetConfig;
      } else {
        ({ data, columnWidths } = sheetConfig);
      }

      const ws =
        Array.isArray(data) && Array.isArray(data[0])
          ? XLSX.utils.aoa_to_sheet(data)
          : XLSX.utils.json_to_sheet(data);

      if (columnWidths && Array.isArray(columnWidths)) {
        ws["!cols"] = columnWidths.map((w) => ({ wch: w }));
      }

      XLSX.utils.book_append_sheet(wb, ws, name);
    });
  }

  // Write the file
  XLSX.writeFile(wb, fileName);
};

export const generateExcelTemplate = ({
  fileName,
  instructions,
  sampleData,
  includeInstructions = true,
  includeSampleData = true
}) => {
  if (!fileName) fileName = "upload_template.xlsx";

  const dataSheet =
    includeSampleData && sampleData && sampleData.length > 0
      ? XLSX.utils.json_to_sheet(sampleData)
      : sampleData && sampleData.length > 0
        ? XLSX.utils.aoa_to_sheet([Object.keys(sampleData[0])])
        : XLSX.utils.json_to_sheet([]);
  const wb = XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(wb, dataSheet, "Template");
  if (includeInstructions && instructions && instructions.length > 0) {
    const instructionsSheet = XLSX.utils.aoa_to_sheet(instructions);
    XLSX.utils.book_append_sheet(wb, instructionsSheet, "Instructions");
  }

  XLSX.writeFile(wb, fileName);
};

export const validateExcelData = (data, validationSchema) => {
  const errors = [];
  const validRows = [];

  data.forEach((row, index) => {
    let rowErrors = [];

    if (row.length <= 0) return;

    validationSchema.forEach((rule, colIndex) => {
      const value = row[colIndex];
      const isEmpty =
        value == null || (typeof value === "string" && value.trim() === "");

      if (rule.required && isEmpty) {
        rowErrors.push(`Column ${colIndex + 1} is required.`);
      }

      if (!rule.required && isEmpty) return;

      if (
        rule.type === "text" &&
        typeof value !== "string"
      ) {
        rowErrors.push(`Column ${colIndex + 1} must be text.`);
      }

      if (
        rule.type === "date" &&
        !parseExcelDate(value)
      ) {
        rowErrors.push(`Column ${colIndex + 1} must be a valid date.`);
      }

      if (
        rule.type === "number" &&
        !isEmpty &&
        !(
          (typeof value === "number" || typeof value === "string") &&
          Number.isFinite(Number(value))
        )
      ) {
        rowErrors.push(`Column ${colIndex + 1} must be a number.`);
      }

      if (
        rule.type === "boolean" &&
        ![true, false, "true", "false", "TRUE", "FALSE"].includes(value)
      ) {
        rowErrors.push(`Column ${colIndex + 1} must be true or false.`);
      } else if (rule.type === "boolean" && typeof row === "string") {
        const lowerValue = value.trim().toLowerCase();
        if (lowerValue === "true") row = true;
        if (lowerValue === "false") row = false;
      }

      if (Array.isArray(rule.fixedValue) && !rule.fixedValue.includes(value)) {
        rowErrors.push(
          `Column ${colIndex + 1} must be one of [${rule.fixedValue.join(
            ", "
          )}]. Found: '${value}'`
        );
      }
    });

    if (rowErrors.length > 0) {
      errors.push(`Row ${index + 1}: ${rowErrors.join(" | ")}`);
    } else {
      validRows.push(row);
    }
  });

  return { validRows, errors };
};
